import React, { useState, useEffect } from 'react';
import {
  Flex,
  HStack,
  VStack,
  IconButton,
  Button,
  Image,
  Text,
  Box,
  useBreakpointValue,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import autoMdLogoUpdated from '../Assets/icons/AutoFIY.svg';
import humberger_new_icon from '../Assets/icons/humberger_new_icon.svg';
import { useAuthStore } from '../store/authStore';

const Header = ({
  onMenuClick,
  isChatView,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnDashboard = location?.pathname?.startsWith('/dashboard');
  const { hasToken } = useAuthStore();
  const [username, setUsername] = useState(() => {
    const token = localStorage.getItem('token');
    if (token) {
      return localStorage.getItem('username') || 'User';
    }
    return '';
  });

  useEffect(() => {
    const updateUsername = () => {
      if (hasToken) {
        const storedUsername = localStorage.getItem('username') || 'User';
        setUsername(storedUsername);
      } else {
        setUsername('');
      }
    };

    updateUsername();

    const handleStorageChange = () => {
      updateUsername();
    };

    window.addEventListener('localStorageChange', handleStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, [hasToken]);

  // Responsive sizes
  const logoHeight = useBreakpointValue({ base: '20px', md: '25px' });
  const paddingX = useBreakpointValue({ base: 3, md: 6 });
  const paddingY = useBreakpointValue({ base: 3, md: 4 });
  const usernameFont = useBreakpointValue({ base: 'xs', md: 'sm' });
  const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });

  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      bg="#212e89"
      px={paddingX}
      py={paddingY}
      borderBottom="1px solid"
      borderColor="gray.200"
      w="100%"
      top={0}
      zIndex={1000}
      css={css`
        position: -webkit-sticky; /* iOS Safari support (older versions) */
        position: sticky; /* Standard for modern browsers */
        -webkit-transform: translate3d(0, 0, 0); /* Force hardware acceleration on iOS */
        transform: translate3d(0, 0, 0); /* Force hardware acceleration */
        -webkit-backface-visibility: hidden; /* Improve rendering performance on iOS */
        backface-visibility: hidden;
      `}
    >
      {/* Left section: menu + logo */}
      <Box>
        <HStack align="start" spacing={3}>
          {/* Hamburger Icon */}
          {hasToken && (
            <IconButton
              icon={<Image src={humberger_new_icon} alt="Menu" w={6} h={6} />}
              variant="ghost"
              onClick={onMenuClick}
              aria-label="Menu"
              color="white"
              _hover={{ bg: 'rgba(255,255,255,0.1)' }}
              size={buttonSize}
            />
          )}

          {/* Logo */}
          <Box
              onClick={() => navigate(isOnDashboard ? '/' : '/dashboard')}
              cursor="pointer"
              role="button"
              aria-label="Navigate via logo"
            >
              <Image
                src={autoMdLogoUpdated}
                alt="AutoFIY Logo"
                h={logoHeight}
                w="auto"
              />
          </Box>
        </HStack>
      </Box>

      {/* Right section: settings button + username */}
      {hasToken && (
        <>
          {/* Mobile view: Settings button with username below */}
          <VStack
            align="flex-end"
            spacing={1}
            display={{ base: 'flex', md: 'none' }}
          >
            <Button
              bg="#3182CE"
              color="white"
              size={buttonSize}
              px={3}
              py={0.5}
              _hover={{ bg: '#2B6CB0' }}
              onClick={() => navigate('/dashboard/settings')}
            >
              Settings
            </Button>
            <Text
              fontSize={usernameFont}
              color="white"
              fontWeight="medium"
            >
              Welcome, {username}
            </Text>
          </VStack>
          
          {/* Desktop view: Username and Settings button side by side */}
          <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
            <Text
              fontSize={usernameFont}
              color="white"
              fontWeight="medium"
            >
              Welcome, {username}
            </Text>
            <Button
              bg="#3182CE"
              color="white"
              size={buttonSize}
              px={6}
              py={0.5}
              _hover={{ bg: '#2B6CB0' }}
              onClick={() => navigate('/dashboard/settings')}
            >
              Settings
            </Button>
          </HStack>
        </>
      )}

      {!hasToken && isOnDashboard && (
        <HStack spacing={3}>
          <Button
            bg="#1A73E8"
            color="white"
            borderRadius="lg"
            size={buttonSize}
            px={{ base: 5, md: 7 }}
            py={2}
            fontWeight="medium"
            fontFamily="sans-serif"
            _hover={{ bg: '#1557B0' }}
            _active={{ bg: '#1348A0' }}
            onClick={() => navigate('/sign-in')}
          >
            Log in
          </Button>
          <Button
            bg="#1A73E8"
            color="white"
            borderRadius="lg"
            size={buttonSize}
            px={{ base: 5, md: 7 }}
            py={2}
            fontWeight="medium"
            fontFamily="sans-serif"
            _hover={{ bg: '#1557B0' }}
            _active={{ bg: '#1348A0' }}
            onClick={() => navigate('/sign-up')}
          >
            Sign up
          </Button>
        </HStack>
      )}

    </Flex>
  );
};

export default Header;
